import { useEffect, useState } from "react";
import { Loading } from "../../components/Loading";
import { useParams, useNavigate } from "react-router-dom";
import { useFirebaseAuth } from "../../hooks/useFirebaseAuth";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Divider,
  Switch,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Add,
  Close,
  Edit,
  Description,
  Visibility,
  Payment,
} from "@mui/icons-material";
import axios from "axios";
import { useToast } from "../../components/Toast";
import { uploadFile, LocalUser } from "../../utils/UploadFile";
import { UploadedFile } from "../../components/FileUploadSection";
import { fetchDocuments } from "../../utils/FetchDocuments";
import LoanPayments from "../../components/LoanPayments";
import LoanForm from "../../components/LoanForm";
import { DocumentUploadSection } from "../../components/DocumentUploadSection";
import NavigationHeader from "../../components/NavigationHeader";
import { formatCurrency, formatDate, formatText } from "../../utils/utils";
import VehicleFinanceSummary from "./VehicleFinanceSummary";
import { getValidToken } from "../../utils/authUtils";
import { commonButtonProps, commonTextFieldProps } from "../../utils/styles";
import { usePlanStatus } from "../../hooks/usePlanStatus";
import { PlanStatus } from "../../utils/alertUtils";
import { LoanDetail } from "../../types/loan";

interface LoanPayment {
  id: string;
  loanId: string;
  month: number;
  startDate: string;
  endDate: string;
  amount: number;
  isPaid: boolean;
  paymentDate: string;
  createdAt: string;
  updatedAt: string;
}

interface LoanApiResponse {
  isSuccess: boolean;
  data: LoanDetail;
}

const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

const VehicleFinance = () => {
  const { vehicleId } = useParams();
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : "";
  const { user, loading } = useFirebaseAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [financeInfo, setFinanceInfo] = useState<LoanDetail[] | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [vehicleDetails, setVehicleDetails] = useState({
    vehicleCost: 0,
    isFinanced: false,
  });
  const [editedVehicleDetails, setEditedVehicleDetails] = useState({
    vehicleCost: 0,
    isFinanced: false,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState<LoanDetail | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedFile[]>(
    []
  );
  const [isUploading, setIsUploading] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);
  const [loanTypes, setLoanTypes] = useState<string[]>([
    "chassis",
    "body",
    "chassis+body",
    "other",
  ]);

  const { planStatus } = usePlanStatus();

  useEffect(() => {
    if (user && !loading) {
      fetchFinanceData(vehicleId as string);
    }
  }, [user, loading, navigate]);

  const fetchFinanceData = async (vehicleId: string) => {
    setIsLoading(true);
    try {
      const token = await getValidToken();

      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST}vehicle-finance/${vehicleId}/loans`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const vehicleDetailsResponse = response?.data?.data?.vehicle;
      setVehicleDetails({
        vehicleCost: vehicleDetailsResponse?.vehicleCost || 0,
        isFinanced: vehicleDetailsResponse?.isFinanced,
      });
      setEditedVehicleDetails({
        vehicleCost: vehicleDetailsResponse?.vehicleCost || 0,
        isFinanced: vehicleDetailsResponse?.isFinanced,
      });
      const financeInfoResponse = response?.data?.data?.loans;
      setFinanceInfo(financeInfoResponse);

      // Filter out loan types that are already used
      const usedLoanTypes = new Set(
        financeInfoResponse?.map((loan: LoanDetail) => loan.type) || []
      );
      const availableLoanTypes = [
        "chassis",
        "body",
        "chassis+body",
        "other",
      ].filter((type) => !usedLoanTypes.has(type));
      setLoanTypes(availableLoanTypes);
    } catch (error) {
      showToast("Failed to fetch finance data", "error");
      console.error("Error fetching finance data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpload = async (loanId: string) => {
    if (!selectedFiles.length || !loanId) return;

    setIsSubmitting(true);
    try {
      const uploadPromises = selectedFiles.map((file) =>
        uploadFile({
          file,
          documentType: "vehicle-loan",
          documentTypeId: loanId,
          documentContext: "vehicle",
          description: selectedLoan?.type || "loan",
          companyId: companyId,
        })
      );

      await Promise.all(uploadPromises);
      await fetchFinanceData(vehicleId as string);
      setSelectedFiles([]);
    } catch (error) {
      console.error("Error uploading files:", error);
      showToast("Failed to upload documents", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (
    values: LoanDetail
  ): Promise<LoanDetail | null> => {
    if (Number(values.totalCost) <= 0) {
      showToast("Total cost should be greater than 0", "error");
      return null;
    }
    if (Number(values.amount) <= 0) {
      showToast("Loan amount should be greater than 0", "error");
      return null;
    }
    // emidate is same or after loanstartdate and before loanenddate
    if (
      new Date(values.emiDate) < new Date(values.startDate) ||
      new Date(values.emiDate) > new Date(values.endDate)
    ) {
      showToast(
        "EMI date should be same or after loan start date and before loan end date",
        "error"
      );
      return null;
    }

    // loan end date should be after loan start date
    if (new Date(values.endDate) < new Date(values.startDate)) {
      showToast("Loan end date should be after loan start date", "error");
      return null;
    }

    const enrichedValues = {
      ...values,
      ...(values.type === "other" && {
        type: values.otherType,
      }),
      vehicleCost: vehicleDetails.vehicleCost,
    };
    setIsSubmitting(true);
    try {
      let response;
      const token = await getValidToken();
      if (selectedLoan?.id) {
        // Update existing loan
        response = await axios.put<LoanApiResponse>(
          `${process.env.REACT_APP_API_HOST}vehicle-finance/loans/${selectedLoan.id}`,
          enrichedValues,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        await handleUpload(selectedLoan.id);
        if (response?.data?.isSuccess) {
          showToast("Loan updated successfully", "success");
          fetchFinanceData(vehicleId as string);
        } else {
          showToast("Failed to update loan", "error");
        }
      } else {
        // Create new loan
        response = await axios.post<LoanApiResponse>(
          `${process.env.REACT_APP_API_HOST}vehicle-finance/${vehicleId}/loans`,
          enrichedValues,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response?.data?.isSuccess) {
          await handleUpload(response?.data?.data?.id || "");
          fetchFinanceData(vehicleId as string);
          showToast("Finance data submitted successfully", "success");
        } else {
          showToast("Failed to submit finance data", "error");
        }
      }
      handleCloseDialog();
      return response?.data?.data || null;
    } catch (error) {
      console.error("Error submitting finance data:", error);
      showToast("Failed to submit finance data", "error");
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVehicleDetailsSave = async () => {
    if (!planStatus?.permissions?.canCreateVehicleFinance) {
      showToast("You don't have permission to edit vehicle details", "warning");
      return;
    }
    const vehicleCost = Number(editedVehicleDetails.vehicleCost);
    try {
      const token = await getValidToken();
      const response = await axios.put(
        `${process.env.REACT_APP_API_HOST}vehicles/${vehicleId}`,
        {
          vehicleCost: vehicleCost,
          isFinanced: editedVehicleDetails.isFinanced,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      showToast("Vehicle details updated successfully", "success");
      fetchFinanceData(vehicleId as string);
      setIsEditing(false);
      setVehicleDetails({ vehicleCost: 0, isFinanced: false });
      setEditedVehicleDetails({ vehicleCost: 0, isFinanced: false });
    } catch (error) {
      console.error("Error updating vehicle details:", error);
      showToast("Failed to update vehicle details", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (loan?: LoanDetail, vehicleCost?: number) => {
    const loanWithVehicleCost = { ...loan, vehicleCost } as LoanDetail;
    setSelectedLoan(loanWithVehicleCost || null);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedLoan(null);
    setSelectedFiles([]);
  };

  const fetchLoanDocuments = async (loanId: string | undefined) => {
    if (!loanId) return;

    try {
      const docs = await fetchDocuments({
        documentContext: "vehicle",
        documentTypeId: loanId,
        documentType: "vehicle-loan",
      });

      setUploadedDocuments(
        docs.map((doc) => ({
          id: doc.id,
          fileName: doc.fileName,
          presignedUrl: doc.presignedUrl,
        }))
      );
    } catch (error) {
      console.error("Error fetching loan documents:", error);
    }
  };

  const handlePreview = (url: string) => {
    window.open(url, "_blank");
  };

  const handleUploadDocuments = async () => {
    if (!selectedLoan?.id || selectedFiles.length === 0) return;

    setIsUploading(true);
    try {
      const uploadPromises = selectedFiles.map((file) =>
        uploadFile({
          file,
          documentType: "vehicle-loan",
          documentTypeId: selectedLoan.id || "",
          documentContext: "vehicle",
          description: selectedLoan.type || "loan",
          companyId: companyId,
        })
      );

      await Promise.all(uploadPromises);
      showToast("Documents uploaded successfully", "success");
      setSelectedFiles([]);
      setOpenUpload(false);
      await fetchLoanDocuments(selectedLoan?.id);
    } catch (error) {
      console.error("Error uploading documents:", error);
      showToast("Failed to upload documents", "error");
    } finally {
      setIsUploading(false);
    }
  };

  // Add useEffect to fetch documents when selectedLoan changes
  useEffect(() => {
    if (selectedLoan?.id) {
      fetchLoanDocuments(selectedLoan?.id);
    }
  }, [selectedLoan?.id]);

  if (loading || isLoading) {
    return <Loading message="Loading vehicle details..." fullScreen={true} />;
  }

  if (isSubmitting) {
    return (
      <Loading
        message={`${
          selectedLoan?.id ? "Updating" : "Submitting"
        } finance data...`}
        fullScreen={true}
      />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <NavigationHeader
        title="Vehicle Finance"
        breadcrumbs={[
          { label: "Vehicles", path: "/vehicles" },
          { label: "Vehicle Details", path: `/vehicles/${vehicleId}` },
          { label: "Vehicle Finance" },
        ]}
        button={
          vehicleDetails.isFinanced &&
          planStatus?.permissions?.canCreateVehicleFinance
            ? {
                label: "Add New Loan Detail",
                icon: <Add />,
                onClick: () =>
                  handleOpenDialog(undefined, vehicleDetails.vehicleCost),
                variant: "contained",
                color: "primary",
              }
            : null
        }
      />

      <Box mb={2}>
        <Paper sx={{ p: 2 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box width="100%">
              <Typography variant="h5" gutterBottom fontSize="0.8rem">
                Vehicle Purchase Amount
              </Typography>
              {!isEditing && vehicleDetails.vehicleCost > 0 ? (
                <Box>
                  <Typography variant="h3" gutterBottom fontSize="0.8rem">
                    {formatCurrency(vehicleDetails.vehicleCost)}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography
                      variant="subtitle1"
                      color="text.secondary"
                      fontSize="0.75rem"
                    >
                      Finance Status:
                    </Typography>
                    <Chip
                      label={
                        vehicleDetails.isFinanced ? "Financed" : "Not Financed"
                      }
                      color={vehicleDetails.isFinanced ? "success" : "default"}
                      size="small"
                    />
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Box display="flex" gap={2} alignItems="center">
                    <TextField
                      value={editedVehicleDetails.vehicleCost || ""}
                      onChange={(e) =>
                        setEditedVehicleDetails((prev) => ({
                          ...prev,
                          vehicleCost:
                            e.target.value === "" ? 0 : Number(e.target.value),
                        }))
                      }
                      type="number"
                      placeholder="Enter amount"
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ mr: 1, color: "text.primary" }}>
                            ₹
                          </Typography>
                        ),
                      }}
                      fullWidth
                      {...commonTextFieldProps}
                    />
                    {editedVehicleDetails.vehicleCost > 0 && (
                      <Button
                        variant="outlined"
                        onClick={() => setIsEditing(false)}
                        {...commonButtonProps}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      onClick={() => handleVehicleDetailsSave()}
                      disabled={
                        !editedVehicleDetails.vehicleCost ||
                        Number(editedVehicleDetails.vehicleCost) <= 0
                      }
                      {...commonButtonProps}
                    >
                      Save
                    </Button>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body1" fontSize="0.75rem">
                      Vehicle is financed?
                    </Typography>
                    <Typography
                      variant="body1"
                      fontSize="0.75rem"
                      color={
                        !editedVehicleDetails.isFinanced
                          ? "text.primary"
                          : "text.secondary"
                      }
                      sx={{ minWidth: 30 }}
                    >
                      No
                    </Typography>
                    <Switch
                      checked={editedVehicleDetails.isFinanced}
                      onChange={(e) =>
                        setEditedVehicleDetails((prev) => ({
                          ...prev,
                          isFinanced: e.target.checked,
                        }))
                      }
                      color="success"
                    />
                    <Typography
                      variant="body1"
                      fontSize="0.75rem"
                      color={
                        editedVehicleDetails.isFinanced
                          ? "text.primary"
                          : "text.secondary"
                      }
                      sx={{ minWidth: 30 }}
                    >
                      Yes
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {!isEditing &&
              vehicleDetails.vehicleCost > 0 &&
              planStatus?.permissions?.canEditVehicleFinance && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setIsEditing(true)}
                  {...commonButtonProps}
                >
                  Edit
                </Button>
              )}
          </Box>
        </Paper>
      </Box>

      {vehicleDetails.isFinanced && (
        <>
          <VehicleFinanceSummary financeInfo={financeInfo || []} />
          {financeInfo && financeInfo?.length > 0 ? (
            <Stack spacing={2}>
              {financeInfo?.map((loan, index) => (
                <LoanView
                  key={index}
                  data={loan}
                  handleOpenDialog={(loan) =>
                    handleOpenDialog(loan, vehicleDetails.vehicleCost)
                  }
                  planStatus={planStatus}
                />
              ))}
            </Stack>
          ) : (
            <Paper sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h6" color="text.secondary" fontSize="0.8rem">
                No finance details available
              </Typography>
            </Paper>
          )}
        </>
      )}

      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ py: 0.5 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              fontSize: "1rem",
            }}
          >
            <Typography fontSize="0.8rem">
              {selectedLoan?.id ? "Update Loan Detail" : "Add New Loan Detail"}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <LoanForm
          onSubmit={handleSubmit}
          onClose={handleCloseDialog}
          initialData={selectedLoan || undefined}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          loanTypes={loanTypes}
        />
      </Dialog>

      <Dialog
        open={openUpload}
        onClose={() => {
          setOpenUpload(false);
          setSelectedFiles([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "0.8rem" }}>Upload Document</DialogTitle>
        <DialogContent dividers>
          <DocumentUploadSection
            title="Loan Documents"
            selectedFiles={selectedFiles}
            uploadedFiles={uploadedDocuments}
            onFileSelect={(files: File[]) =>
              setSelectedFiles((prev) => [...prev, ...files])
            }
            onFileRemove={(index: number) =>
              setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
            }
            onPreview={handlePreview}
            isLoading={isUploading}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenUpload(false);
              setSelectedFiles([]);
            }}
            {...commonButtonProps}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={selectedFiles.length === 0 || isUploading}
            onClick={handleUploadDocuments}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VehicleFinance;

const LoanView = ({
  data,
  handleOpenDialog,
  planStatus,
}: {
  data: LoanDetail;
  handleOpenDialog: (loan?: LoanDetail) => void;
  planStatus: PlanStatus;
}) => {
  const [documents, setDocuments] = useState<UploadedFile[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [openUpload, setOpenUpload] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const { showToast } = useToast();
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : null;

  useEffect(() => {
    if (data.id) {
      fetchLoanDocuments();
    }
  }, [data.id]);

  const fetchLoanDocuments = async () => {
    try {
      const docs = await fetchDocuments({
        documentContext: "vehicle",
        documentTypeId: data.id || "",
        documentType: "vehicle-loan",
      });

      setDocuments(
        docs.map((doc) => ({
          id: doc.id,
          fileName: doc.fileName,
          presignedUrl: doc.presignedUrl,
        }))
      );
    } catch (error) {
      console.error("Error fetching loan documents:", error);
    }
  };

  const handlePreview = (url: string) => {
    window.open(url, "_blank");
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleUploadDocuments = async () => {
    if (!data.id || selectedFiles.length === 0) return;

    setIsUploading(true);
    try {
      const uploadPromises = selectedFiles.map((file) =>
        uploadFile({
          file,
          documentType: "vehicle-loan",
          documentTypeId: data.id || "",
          documentContext: "vehicle",
          description: data.type || "loan",
          companyId: companyId,
        })
      );

      await Promise.all(uploadPromises);
      showToast("Documents uploaded successfully", "success");
      setSelectedFiles([]);
      setOpenUpload(false);
      await fetchLoanDocuments();
    } catch (error) {
      console.error("Error uploading documents:", error);
      showToast("Failed to upload documents", "error");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 2, borderRadius: 2, boxShadow: 3 }}>
      <Grid container spacing={1}>
        <Grid
          item
          xs={12}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6" component="h2" fontSize="0.8rem">
              {formatText(data.type)} Loan
            </Typography>
            <Chip
              label={`${data.duration} months`}
              size="small"
              sx={{ borderRadius: 1 }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              fontSize="0.75rem"
            >
              {new Date(data.emiDate).getDate()}
              {getOrdinalSuffix(new Date(data.emiDate).getDate())} of every
              month
            </Typography>
          </Box>
          {planStatus?.permissions?.canEditVehicleFinance && (
            <IconButton
              onClick={() => handleOpenDialog(data)}
              sx={{ color: "primary.main" }}
            >
              <Edit />
            </IconButton>
          )}
        </Grid>
        <Grid item xs={12} sm={12}>
          <Divider />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box>
            <Typography color="text.secondary" gutterBottom fontSize="0.75rem">
              Provider
            </Typography>
            <Typography variant="h6" fontSize="0.75rem">
              {data.provider}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box>
            <Typography color="text.secondary" gutterBottom fontSize="0.75rem">
              Loan Period
            </Typography>
            <Typography variant="h6" fontSize="0.75rem">
              {formatDate(data.startDate)} - {formatDate(data.endDate)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box>
            <Typography color="text.secondary" gutterBottom fontSize="0.75rem">
              Monthly EMI
            </Typography>
            <Typography variant="h6" fontSize="0.75rem">
              {formatCurrency(data.emi || 0)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box>
            <Typography color="text.secondary" gutterBottom fontSize="0.75rem">
              Total Cost
            </Typography>
            <Typography variant="h6" fontSize="0.75rem">
              {formatCurrency(data.totalCost || 0)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box>
            <Typography color="text.secondary" gutterBottom fontSize="0.75rem">
              Down Payment
            </Typography>
            <Typography variant="h6" fontSize="0.75rem">
              {formatCurrency(data.downPayment || 0)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box>
            <Typography color="text.secondary" gutterBottom fontSize="0.75rem">
              Loan Amount
            </Typography>
            <Typography variant="h6" fontSize="0.75rem">
              {formatCurrency(data.amount || 0)}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              minHeight: "30px",
            }}
          >
            <Tab
              icon={<Description style={{ fontSize: 12 }} />}
              iconPosition="start"
              label="Documents"
              sx={{ fontSize: "0.75rem", px: 2, py: 0 }}
            />
            <Tab
              icon={<Payment style={{ fontSize: 12 }} />}
              iconPosition="start"
              label="Payments"
              sx={{ fontSize: "0.75rem", px: 2, py: 0 }}
            />
          </Tabs>

          {/* Documents Tab */}
          {activeTab === 0 && (
            <Box sx={{ p: 2 }}>
              {documents?.length > 0 ? (
                documents?.map((document) => (
                  <Box
                    key={document.id}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{ mb: 1 }}
                  >
                    <Description color="primary" style={{ fontSize: 12 }} />
                    <Typography
                      fontSize="0.75rem"
                      onClick={() => handlePreview(document?.presignedUrl)}
                      sx={{
                        cursor: "pointer",
                        fontStyle: "italic",
                        textDecoration: "underline",
                      }}
                    >
                      {document?.fileName}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary" fontSize="0.75rem">
                  No documents uploaded
                </Typography>
              )}
              {planStatus?.permissions?.canEditVehicleFinance &&
                planStatus?.permissions?.canCreateVehicleFinance && (
                  <Box
                    display={"flex"}
                    justifyContent={"flex-end"}
                    sx={{ mt: 2 }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setOpenUpload(true)}
                      {...commonButtonProps}
                    >
                      Upload Documents
                    </Button>
                  </Box>
                )}
            </Box>
          )}

          {/* Payments Tab */}
          {activeTab === 1 && (
            <LoanPayments data={data} planStatus={planStatus} />
          )}
        </Grid>

        <Dialog
          open={openUpload}
          onClose={() => {
            setOpenUpload(false);
            setSelectedFiles([]);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ fontSize: 16 }}>Upload Documents</DialogTitle>
          <DialogContent dividers>
            <DocumentUploadSection
              title="Loan Documents"
              selectedFiles={selectedFiles}
              uploadedFiles={documents}
              onFileSelect={(files: File[]) =>
                setSelectedFiles((prev) => [...prev, ...files])
              }
              onFileRemove={(index: number) =>
                setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
              }
              onPreview={handlePreview}
              isLoading={isUploading}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenUpload(false);
                setSelectedFiles([]);
              }}
              color="error"
              variant="outlined"
              {...commonButtonProps}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={selectedFiles.length === 0 || isUploading}
              onClick={handleUploadDocuments}
              {...commonButtonProps}
            >
              Upload
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Paper>
  );
};
